import { FC, useEffect, useRef, useState } from 'react';
import { DownloadIcon, LoadingSpinner, TrashIcon, globalToastService } from '@repo/atomic-components';
import { useAzureFileUpload } from '@repo/file-uploader';
import {
  TemplateNode,
  TemplateType,
  useGetTemplateListQuery,
  useLazyGetTemplateDownloadEmptyQuery,
  useLazyPostTemplateDeleteQuery,
} from '@/api';
import { formatDateString } from '@/utils';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { TableIconButton } from '@/ui/components/TableIconButton';
import { DashboardSection } from './DashboardSection';

interface TemplatesListSectionProps {
  templateType: TemplateType;
  onChangeTemplateType: (templateType: TemplateType) => void;
  /** Bumped by the parent after an upload to force a refetch. */
  refreshKey: number;
}

const TYPE_TABS: { label: string; value: TemplateType }[] = [
  { label: 'Chronology', value: TemplateType.CHRONOLOGY },
  { label: 'Index', value: TemplateType.INDEX },
];

/**
 * Fourth dashboard section — lists the already-available templates for the
 * selected type and lets a global admin delete them. A type toggle switches
 * between Chronology and Index; the parent keeps the selected type in sync with
 * the upload section so a fresh upload refetches the right list.
 */
export const TemplatesListSection: FC<TemplatesListSectionProps> = ({
  templateType,
  onChangeTemplateType,
  refreshKey,
}) => {
  const { data, isFetching, isError, refetch } = useGetTemplateListQuery(
    { templateType },
    { refetchOnMountOrArgChange: true },
  );
  const [triggerDelete] = useLazyPostTemplateDeleteQuery();
  const [triggerDownloadEmpty] = useLazyGetTemplateDownloadEmptyQuery();
  const { downloadFile } = useAzureFileUpload();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Refetch whenever the parent signals a new upload (refreshKey bump). The
  // initial mount is already handled by refetchOnMountOrArgChange, so skip
  // the first run to avoid a redundant duplicate request.
  const previousRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (previousRefreshKey.current === refreshKey) return;
    previousRefreshKey.current = refreshKey;
    refetch();
  }, [refreshKey, refetch]);

  const templates = data?.templates ?? [];

  const handleDelete = (template: TemplateNode) => {
    setDeletingId(template.templateId);
    triggerDelete({ templateType, templateId: template.templateId })
      .unwrap()
      .then(() => {
        globalToastService.show({
          severity: 'success',
          summary: 'Template deleted',
          detail: `${template.templateName} deleted.`,
        });
        refetch();
      })
      .catch((error) => {
        console.error('Failed to delete template:', error);
        globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete template.' });
      })
      .finally(() => {
        setDeletingId(null);
        setPendingDeleteId(null);
      });
  };

  const handleDownload = (template: TemplateNode) => {
    setDownloadingId(template.templateId);
    triggerDownloadEmpty({ templateType, templateId: template.templateId })
      .unwrap()
      .then(({ downloadPath, downloadToken }) => {
        // Same download path used across the app — hand the SAS link to the
        // Azure uploader, which streams the blob down as `templateName`.
        downloadFile(template.templateName, downloadPath, downloadToken);
      })
      .catch((error) => {
        console.error('Failed to download template:', error);
        globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to download template.' });
      })
      .finally(() => {
        setDownloadingId(null);
      });
  };

  const tabClass = (active: boolean) =>
    `flex h-8 items-center rounded-full border px-3 text-sm font-semibold whitespace-nowrap transition-colors ${
      active
        ? 'border-fletchers-green-600 bg-fletchers-green-50 text-fletchers-green-600'
        : 'border-fletchers-gray-110 text-fletchers-black-900 hover:bg-fletchers-gray-50'
    }`;

  return (
    <DashboardSection
      title="Available templates"
      description="Existing custom templates for this organisation."
      action={
        <OutlineButton
          label="Refresh"
          icon="pi pi-refresh"
          onClick={() => refetch()}
          disabled={isFetching}
          style={{ height: 32 }}
        />
      }
    >
      <div className="flex items-center gap-2">
        {TYPE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={tabClass(tab.value === templateType)}
            onClick={() => onChangeTemplateType(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="border-fletchers-gray-110 overflow-hidden rounded-md border">
        {isFetching ? (
          <div className="flex h-40 items-center justify-center">
            <LoadingSpinner size={40} />
          </div>
        ) : isError ? (
          <div className="text-fletchers-red-500 flex h-24 items-center justify-center text-sm">
            Failed to load templates.
          </div>
        ) : templates.length === 0 ? (
          <div className="text-fletchers-gray-200 flex h-24 items-center justify-center text-sm">
            No {templateType.toLowerCase()} templates yet.
          </div>
        ) : (
          <table className="min-w-full">
            <thead>
              <tr className="border-fletchers-gray-110 border-b">
                <th className="text-fletchers-gray-175 px-4 py-2 text-left text-[14px]/[20px] font-[600]">Name</th>
                <th className="text-fletchers-gray-175 px-4 py-2 text-left text-[14px]/[20px] font-[600]">Uploaded</th>
                <th className="text-fletchers-gray-175 px-4 py-2 text-right text-[14px]/[20px] font-[600]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.templateId} className="border-fletchers-gray-110 border-b last:border-b-0">
                  <td className="text-fletchers-black-900 px-4 py-2 text-sm">{template.templateName}</td>
                  <td className="text-fletchers-gray-200 px-4 py-2 text-sm">
                    {template.uploadedAt ? formatDateString(template.uploadedAt) : 'Pending'}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-2">
                      {pendingDeleteId === template.templateId ? (
                        <>
                          <span className="text-fletchers-gray-200 text-xs">Delete?</span>
                          <OutlineButton
                            label="Cancel"
                            onClick={() => setPendingDeleteId(null)}
                            disabled={deletingId === template.templateId}
                            style={{ height: 28, paddingTop: 0, paddingBottom: 0 }}
                          />
                          <button
                            type="button"
                            onClick={() => handleDelete(template)}
                            disabled={deletingId === template.templateId}
                            className="hover:bg-fletchers-red-500/10 text-fletchers-red-500 flex h-7 items-center gap-1 rounded-md px-2 text-sm font-semibold disabled:opacity-50"
                          >
                            {deletingId === template.templateId ? (
                              <i className="pi pi-spin pi-spinner text-xs" aria-hidden />
                            ) : (
                              <i className="pi pi-trash text-xs" aria-hidden />
                            )}
                            Delete
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Download then delete — same 32×32 bordered chip
                              buttons as the Medical Records / Case tables. */}
                          <TableIconButton
                            title="Download template"
                            aria-label={`Download ${template.templateName}`}
                            disabled={downloadingId === template.templateId}
                            onClick={() => handleDownload(template)}
                          >
                            {downloadingId === template.templateId ? (
                              <i className="pi pi-spin pi-spinner text-fletchers-black-900 text-xs" aria-hidden />
                            ) : (
                              <DownloadIcon className="text-fletchers-black-900" />
                            )}
                          </TableIconButton>
                          <TableIconButton
                            title="Delete template"
                            aria-label={`Delete ${template.templateName}`}
                            onClick={() => setPendingDeleteId(template.templateId)}
                          >
                            <TrashIcon className="[&>path]:fill-fletchers-black-900" />
                          </TableIconButton>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardSection>
  );
};
